// Simplified Pet System Types
export interface SimplePet {
  id: string;
  name: string;
  emoji: string;
  stage: 'baby' | 'child' | 'teen' | 'adult' | 'elder';
  happiness: number; // 0-100
  health: number; // 0-100 - new pledge-based health
  totalPoints: number; // Points needed for evolution
  lastInteraction: Date;
  createdAt: Date;
  // Portion system (max 3, +1 every 8 hours)
  portionTimestamps: Date[]; // timestamps of consumed portions awaiting refill
  // Pledge-based fields
  pledgeSheetId?: string;
  dailyTargetProgress: {
    sets: number;
    openings: number;
    closings: number;
    closed: number;
  };
  weeklyPledgeProgress: {
    sets: number;
    openings: number;
    closings: number;
    closed: number;
  };
  lastDailyUpdate: Date;
  lastWeeklyUpdate: Date;
  pledgePerformanceStreak: number; // Days of meeting daily targets
  // Customization
  hat?: 'basic_hat' | 'royal_crown' | null;
  accessory?: 'magic_cape' | null;
  // Food & Economy System
  activityCredits: number;
  hunger: number; // 0-100
  energy: number; // 0-100
}

export interface PetStage {
  name: string;
  emoji: string;
  pointsRequired: number;
  description: string;
}

export const PET_STAGES: Record<SimplePet['stage'], PetStage> = {
  baby: { name: 'Baby', emoji: '🐣', pointsRequired: 0, description: 'Just hatched and ready to grow!' },
  child: { name: 'Child', emoji: '🐤', pointsRequired: 50, description: 'Growing fast with your help!' },
  teen: { name: 'Teen', emoji: '🐦', pointsRequired: 150, description: 'Getting stronger and more confident!' },
  adult: { name: 'Adult', emoji: '🦅', pointsRequired: 300, description: 'Fully grown and magnificent!' },
  elder: { name: 'Elder', emoji: '🦉', pointsRequired: 500, description: 'Wise and powerful companion!' }
};

export const getNextStage = (currentStage: SimplePet['stage']): SimplePet['stage'] | null => {
  const stages: SimplePet['stage'][] = ['baby', 'child', 'teen', 'adult', 'elder'];
  const currentIndex = stages.indexOf(currentStage);
  return currentIndex < stages.length - 1 ? stages[currentIndex + 1] : null;
};

export const canEvolve = (pet: SimplePet): boolean => {
  const nextStage = getNextStage(pet.stage);
  if (!nextStage) return false;
  return pet.totalPoints >= PET_STAGES[nextStage].pointsRequired;
};

export const calculateHappiness = (pet: SimplePet): number => {
  const timeSinceInteraction = Date.now() - pet.lastInteraction.getTime();
  const hoursInactive = timeSinceInteraction / (1000 * 60 * 60);
  
  // Happiness decreases by 1 point per hour, minimum 0
  const happiness = Math.max(0, pet.happiness - Math.floor(hoursInactive));
  return happiness;
};